import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import Pill from '../components/Pill';
import { fmtCost } from '../utils/format';

const stagePillColor = (stage) => {
  if (!stage) return 'muted';
  const s = stage.toLowerCase();
  if (s.includes('receive')) return 'green';
  if (s.includes('void')) return 'red';
  return 'blue';
};

const currentMonthValue = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const ALL_SUPPLIERS = '__all__';

const SupplierPurchaseOrders = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSupplier = searchParams.get('supplier') || '';
  const initialMonth    = searchParams.get('month') || currentMonthValue();

  const [suppliers, setSuppliers]       = useState([]);
  const [supplier, setSupplier]         = useState(initialSupplier);
  const [monthValue, setMonthValue]     = useState(initialMonth);
  const [orders, setOrders]             = useState([]);
  const [receivedOnly, setReceivedOnly] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [loadingOrders, setLoadingOrders]       = useState(false);

  const loadSuppliers = useCallback(async (month) => {
    setLoadingSuppliers(true);
    const { data, error } = await supabase.rpc('get_supplier_monthly_spend', {
      p_month_start: `${month}-01`,
    });
    if (!error) setSuppliers(data || []);
    setLoadingSuppliers(false);
  }, []);

  const loadOrders = useCallback(async (supplierName, month) => {
    if (!supplierName) { setOrders([]); return; }
    setLoadingOrders(true);
    const { data, error } = supplierName === ALL_SUPPLIERS
      ? await supabase.rpc('get_all_purchase_orders_for_month', { p_month_start: `${month}-01` })
      : await supabase.rpc('get_supplier_purchase_orders', {
          p_supplier: supplierName,
          p_month_start: `${month}-01`,
        });
    if (!error) setOrders(data || []);
    setLoadingOrders(false);
  }, []);

  useEffect(() => { loadSuppliers(monthValue); }, [monthValue, loadSuppliers]);
  useEffect(() => { loadOrders(supplier, monthValue); }, [supplier, monthValue, loadOrders]);

  const updateParams = (next) => {
    const params = {};
    if (next.supplier ?? supplier) params.supplier = next.supplier ?? supplier;
    if (next.month ?? monthValue)  params.month    = next.month ?? monthValue;
    setSearchParams(params);
  };

  const handleSupplierChange = (e) => {
    const value = e.target.value;
    setSupplier(value);
    updateParams({ supplier: value });
  };

  const handleMonthChange = (e) => {
    const value = e.target.value;
    setMonthValue(value);
    updateParams({ month: value });
  };

  const displayedOrders = receivedOnly ? orders.filter(o => o.stage === 'Received') : orders;
  const grandTotal      = displayedOrders.reduce((s, o) => s + Number(o.total_owing || 0), 0);
  const monthLabel      = new Date(`${monthValue}-15`).toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' });

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/supplier-spend')}>← Supplier Spend</button>
        <span className="page-nav__title">Supplier Purchase Orders</span>
        <button className="app-header__btn" onClick={() => loadOrders(supplier, monthValue)}>↻ Refresh</button>
      </div>

      <section className="section">
        <SectionHeader
          title={`Purchase Orders — ${monthLabel}`}
          count={supplier ? displayedOrders.length : null}
          onRefresh={() => loadOrders(supplier, monthValue)}
          loading={loadingOrders}
        />
        <p className="section__desc">
          {supplier === ALL_SUPPLIERS
            ? "Every purchase order included in this month's Supplier Spend calculation, across all suppliers."
            : "Individual purchase orders for the selected supplier in the selected month — use this to "
              + "match against a physical/paper invoice if the numbers don't line up."}
        </p>

        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div>
            <label style={{ marginRight: 8, fontWeight: 600 }}>Month:</label>
            <input
              type="month"
              value={monthValue}
              max={currentMonthValue()}
              onChange={handleMonthChange}
              style={{ padding: '6px 10px' }}
            />
          </div>
          <div>
            <label style={{ marginRight: 8, fontWeight: 600 }}>Supplier:</label>
            <select
              value={supplier}
              onChange={handleSupplierChange}
              disabled={loadingSuppliers}
              style={{ padding: '6px 10px', minWidth: 280 }}
            >
              <option value="">— Select a supplier —</option>
              <option value={ALL_SUPPLIERS}>— All Suppliers —</option>
              {suppliers.map(s => (
                <option key={s.supplier} value={s.supplier}>
                  {s.supplier} ({fmtCost(s.total_owing)})
                </option>
              ))}
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={receivedOnly}
              onChange={(e) => setReceivedOnly(e.target.checked)}
            />
            Received Only
          </label>
        </div>

        {!supplier && (
          <div className="empty-row">Select a supplier above to see its purchase orders</div>
        )}

        {supplier && loadingOrders && <div className="loading-row">Loading…</div>}

        {supplier && !loadingOrders && (
          <>
            {!displayedOrders.length && (
              <div className="empty-row">
                {supplier === ALL_SUPPLIERS
                  ? (receivedOnly ? 'No received purchase orders this month' : 'No purchase orders this month')
                  : (receivedOnly ? 'No received purchase orders for this supplier this month' : 'No purchase orders for this supplier this month')}
              </div>
            )}
            {!!displayedOrders.length && (
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <td colSpan={supplier === ALL_SUPPLIERS ? 5 : 4} style={{ fontWeight: 700, textAlign: 'right' }}>Grand Total</td>
                    <td style={{ fontWeight: 700, textAlign: 'right' }}>{fmtCost(grandTotal)}</td>
                  </tr>
                  <tr>
                    <th>Order Ref</th>
                    {supplier === ALL_SUPPLIERS && <th>Supplier</th>}
                    <th>Stage</th>
                    <th>Supplier Invoice #</th>
                    <th>Created</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedOrders.map(o => (
                    <tr key={o.order_id}>
                      <td>{o.order_ref}</td>
                      {supplier === ALL_SUPPLIERS && <td>{o.supplier}</td>}
                      <td><Pill label={o.stage || '—'} color={stagePillColor(o.stage)} /></td>
                      <td>{o.supplier_invoice_ref || '—'}</td>
                      <td>{new Date(o.created_date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      <td style={{ textAlign: 'right' }}>{fmtCost(o.total_owing)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </section>
    </main>
  );
};

export default SupplierPurchaseOrders;
